import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useCatalogContext } from './CatalogContext';
import * as XLSX from 'xlsx';

const CmvImportDialog = () => {
  const {
    cmvImportDialogOpen = false,
    setCmvImportDialogOpen,
    refetch,
    setAlert,
    setMessage,
    setSeverity,
    updateProductPrice: updatePriceFromContext
  } = useCatalogContext();

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [importStatus, setImportStatus] = useState('initial'); // 'initial', 'uploading', 'preview', 'processing', 'success', 'error'
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, valid: 0, invalid: 0 });

  const handleClose = () => {
    setCmvImportDialogOpen(false);
    resetState();
  };

  const resetState = () => {
    setFile(null);
    setFileName('');
    setPreviewData([]);
    setImportStatus('initial');
    setError(null);
    setStats({ total: 0, valid: 0, invalid: 0 });
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setImportStatus('uploading');

      // Processar o arquivo Excel
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          // Validar e transformar dados
          const processedData = validateAndTransformData(jsonData);
          setPreviewData(processedData);
          setImportStatus('preview');
          
          // Calcular estatísticas
          const validItems = processedData.filter(item => item.isValid);
          setStats({
            total: processedData.length,
            valid: validItems.length,
            invalid: processedData.length - validItems.length
          });
        } catch (err) {
          console.error('Erro ao processar arquivo:', err);
          setError('Não foi possível processar o arquivo. Verifique o formato do arquivo e tente novamente.');
          setImportStatus('error');
        }
      };

      reader.onerror = () => {
        setError('Erro ao ler o arquivo.');
        setImportStatus('error');
      };

      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const validateAndTransformData = (jsonData) => {
    // Validar formato dos dados e transformar para o formato necessário
    return jsonData.map((row, index) => {
      const sku = row.sku || row.SKU || row.Sku || row.código || row.codigo || row.CODIGO || '';
      const cmvPrice = row.cmv || row.CMV || row.valor || row.VALOR || row.preco || row.PRECO || row.preço || row.PREÇO || 0;
      
      // Validar os dados
      const isValidSku = !!sku && typeof sku === 'string';
      const isValidPrice = !isNaN(parseFloat(cmvPrice)) && parseFloat(cmvPrice) >= 0;
      
      return {
        id: index,
        sku: sku?.toString().trim(),
        cmvPrice: isValidPrice ? parseFloat(cmvPrice) : null,
        isValid: isValidSku && isValidPrice,
        errors: []
      };
    });
  };

  const handleImport = async () => {
    if (previewData.length === 0) {
      setError('Nenhum dado para importar.');
      return;
    }

    // Filtrar apenas itens válidos
    const validItems = previewData.filter(item => item.isValid);
    
    if (validItems.length === 0) {
      setError('Nenhum item válido para importar.');
      return;
    }

    setImportStatus('processing');

    try {
      // Transformar para o formato esperado pela API
      const apiData = validItems.map(item => ({
        sku: item.sku,
        cmv_price: item.cmvPrice
      }));      // Chamar a API para atualizar os preços
      if (updatePriceFromContext) {
        await updatePriceFromContext(
          { data: apiData },
          {
            onSuccess: () => {
              setImportStatus('success');
              refetch && refetch(); // Recarregar os produtos
              setTimeout(() => {
                handleClose();
                setAlert(true);
                setMessage(`Importação concluída com sucesso! ${validItems.length} preços de CMV atualizados.`);
                setSeverity('success');
              }, 2000);
            },
            onError: (error) => {
              setError(error?.response?.data?.message || 'Erro ao atualizar preços');
              setImportStatus('error');
            }
          }
        );
      } else {
        // Caso a função não esteja disponível no contexto
        setError('Função de atualização de preços não disponível');
        setImportStatus('error');
      }
    } catch (err) {
      setError('Erro ao processar a importação.');
      setImportStatus('error');
    }
  };

  return (
    <Dialog 
      open={cmvImportDialogOpen} 
      onClose={importStatus !== 'processing' ? handleClose : undefined}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUploadIcon sx={{ color: '#f7d0bf' }} />
        Importar Valores de CMV
      </DialogTitle>
      
      <DialogContent>
        {importStatus === 'initial' && (
          <>
            <DialogContentText>
              Importe uma planilha com SKUs e valores de CMV para atualização em massa.
              O formato esperado é uma tabela com colunas "sku" e "cmv".
            </DialogContentText>
            
            <Box sx={{ mt: 3, mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  borderColor: '#f7d0bf',
                  color: 'black',
                  '&:hover': { borderColor: '#f5c1a9', bgcolor: 'rgba(247, 208, 191, 0.1)' }
                }}
              >
                Selecionar Arquivo Excel
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                />
              </Button>
            </Box>
          </>
        )}

        {importStatus === 'uploading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', my: 4 }}>
            <CircularProgress size={50} sx={{ color: "#f7d0bf" }} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Processando o arquivo...
            </Typography>
          </Box>
        )}

        {importStatus === 'preview' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Arquivo: {fileName}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Typography variant="body2">
                  Total de itens: <strong>{stats.total}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  Válidos: <strong>{stats.valid}</strong>
                </Typography>
                {stats.invalid > 0 && (
                  <Typography variant="body2" sx={{ color: 'error.main' }}>
                    Inválidos: <strong>{stats.invalid}</strong>
                  </Typography>
                )}
              </Box>
            </Box>

            {stats.invalid > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Atenção</AlertTitle>
                Há {stats.invalid} itens com dados inválidos que serão ignorados na importação.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Prévia dos dados:
            </Typography>
            
            <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">CMV</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.slice(0, 100).map((row) => (
                    <TableRow key={row.id} sx={{ 
                      backgroundColor: row.isValid ? 'inherit' : 'rgba(255, 0, 0, 0.05)',
                      '&:nth-of-type(odd)': { bgcolor: theme => row.isValid ? theme.palette.action.hover : 'rgba(255, 0, 0, 0.1)' }
                    }}>
                      <TableCell>{row.sku || '—'}</TableCell>
                      <TableCell align="right">
                        {row.cmvPrice !== null ? `R$ ${row.cmvPrice.toFixed(2)}`.replace('.', ',') : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {row.isValid ? (
                          <Typography variant="body2" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 0.5 }} /> 
                            Válido
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'error.main' }}>
                            Inválido
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {previewData.length > 100 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Mostrando 100 de {previewData.length} itens...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {importStatus === 'processing' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', my: 4 }}>
            <CircularProgress size={50} sx={{ color: "#f7d0bf" }} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Atualizando valores de CMV...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Isso pode levar alguns instantes.
            </Typography>
          </Box>
        )}

        {importStatus === 'success' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', my: 4 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'success.main' }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Importação concluída com sucesso!
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
              {stats.valid} valores de CMV foram atualizados.
            </Typography>
          </Box>
        )}

        {importStatus === 'error' && (
          <Alert severity="error" sx={{ my: 2 }}>
            <AlertTitle>Erro</AlertTitle>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {importStatus === 'initial' && (
          <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
            Cancelar
          </Button>
        )}
        
        {importStatus === 'preview' && (
          <>
            <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
              Cancelar
            </Button>
            <Button 
              onClick={resetState} 
              sx={{ color: 'text.secondary' }}
              disabled={importStatus === 'processing'}
            >
              Selecionar outro arquivo
            </Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={stats.valid === 0 || importStatus === 'processing'}
              sx={{
                bgcolor: "#f7d0bf",
                color: "black",
                fontWeight: 600,
                "&:hover": { bgcolor: "#f5c1a9" }
              }}
            >
              Importar {stats.valid} itens
            </Button>
          </>
        )}

        {(importStatus === 'error' || importStatus === 'success') && (
          <Button 
            onClick={handleClose}
            sx={{ color: 'text.secondary' }}
          >
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CmvImportDialog;
